import {
    BarController, BarElement, CategoryScale, Chart, LinearScale,
} from 'chart.js';

class BarChart {
    constructor(element, data, type) {
        this.element = element;
        this.barType = type;
        this.visibleType = ['gas-daily', 'gas-dynamic-daily', 'gas-electricity-daily', 'gas-electricity-dynamic-daily'].includes(type) ? 'gas' : 'electricity';

        this.chart = null;
        this.data = data;

        // The active data (visible bars)
        this.activeData = [];

        this.labels = [];

        this.gas_prices = [];
        this.electricity_prices = [];

        this.higlightIndex = 0;

        // Responsiveness
        this.visibleBarsMobile = 10;
        this.visibleBarsDesktop = 20;
        this.visibleBars = window.matchMedia('(min-width: 992px)').matches ? this.visibleBarsDesktop : this.visibleBarsMobile;

        // Html elements
        this.canvasElement = null;
        this.activeDateElement = null;
        this.reloadButton = null;
        this.showGasToggle = null;
        this.showElectricityToggle = null;

        this.windowKey = `canaryPrices${this.barType.replace(/-/g, '')}`;

        // Colors
        this.defaultBarColor = '#E4F8FF';
        this.activeBarColor = '#006484';
        this.gridLineColor = '#C4E3ED';
        this.electricityColor = '#E4F8FF';
    }

    bootstrap() {
        this.canvasElement = this.element.querySelector('canvas#price-chart');
        this.activePriceElement = this.element.querySelector('[data-replace="active_price"]');
        this.activeDateElement = this.element.querySelector('[data-replace="active-date"]');
        this.titleElement = this.element.querySelector('[data-replace="title"]');

        this.reloadButton = this.element.querySelector('.reload-chart-button');
        this.showGasToggle = this.element.querySelector('#show-gas');
        this.showElectricityToggle = this.element.querySelector('#show-electricity');
        this.reloadButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.loadChart();
        });

        if (this.showGasToggle && this.showElectricityToggle) {
            this.showGasToggle.addEventListener('change', () => {
                this.setVisibleType('gas');
            });
            this.showElectricityToggle.addEventListener('change', () => {
                this.setVisibleType('electricity');
            });
        }
    }

    setVisibleType(type) {
        this.visibleType = type;
        if (this.titleElement) {
            this.titleElement.innerHTML = type === 'gas' ? 'Actuele gasprijs' : 'Actuele stroomprijs';
        }
        this.updateChart();
        this.setInitialActiveState();
    }

    init() {
        this.bootstrap();
        this.initChart();
        this.loadChart();
    }

    setChartData() {
        this.labels = [];
        this.gas_prices = [];
        this.electricity_prices = [];

        const totalItems = this.data.length > this.visibleBars ? this.visibleBars : this.data.length;
        this.activeData = this.data.slice(-totalItems);

        for (let index = 0; index < totalItems; index++) {
            if (this.activeData[index].gas !== null) {
                this.labels.push(new Date(this.activeData[index].date).getDate());
                this.gas_prices.push(parseFloat(this.activeData[index].gas || 1));

                if (this.barType === 'electricity-dynamic-daily' || this.barType === 'gas-electricity-dynamic-daily') {
                    this.electricity_prices.push(parseFloat(this.activeData[index].electricity || 0));
                } else {
                    this.electricity_prices.push(parseFloat(this.activeData[index].electricity_normal || 0));
                }
            }
        }

        if (this.chart) {
            this.chart.data.labels = this.labels;
            this.chart.data.datasets[0].data = this.gas_prices;
            this.chart.data.datasets[1].data = this.electricity_prices;
            if (this.visibleType === 'gas') {
                this.chart.setDatasetVisibility(0, true);
                this.chart.setDatasetVisibility(1, false);
            } else {
                this.chart.setDatasetVisibility(0, false);
                this.chart.setDatasetVisibility(1, true);
            }
        }
    }

    updateChart() {
        if (!this.chart) {
            return;
        }
        this.setChartData();
        this.chart.update();
    }

    updateState() {
        if (this.loading) {
            this.element.classList.add('loading');
        } else {
            this.element.classList.remove('loading');
        }
        if (this.error) {
            this.element.classList.add('error');
        } else {
            this.element.classList.remove('error');
        }
    }

    updateBarColor(activeDatasetIndex, selectedIndex) {
        const dataset = this.chart.data.datasets[activeDatasetIndex];

        const backgroundColors = [];

        // Loop through the data points and set the background color
        for (let i = 0; i < dataset.data.length; i++) {
            if (i === selectedIndex) {
                backgroundColors.push(this.activeBarColor);
            } else {
                backgroundColors.push(this.defaultBarColor);
            }
        }

        dataset.backgroundColor = backgroundColors;

        this.chart.update();
    }

    setActiveBarData(evt) {
        const points = this.chart.getElementsAtEventForMode(evt, 'nearest', { intersect: true }, true);

        if (!points.length) {
            evt.native.target.style.cursor = 'unset';
            return;
        }
        evt.native.target.style.cursor = 'pointer';

        const firstPoint = points[0].index;
        const activeDatasetIndex = points[0].datasetIndex;
        this.setActiveBarDataByIndex(activeDatasetIndex, firstPoint);
    }

    setActiveBarDataByIndex(datasetIndex, barIndex) {
        this.higlightIndex = barIndex;
        const value = this.visibleType === 'gas' ? this.gas_prices[barIndex] : this.electricity_prices[barIndex];
        if (this.activePriceElement) {
            const formattedValue = value.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
            });
            const type = this.visibleType === 'gas' ? ' per m3' : ' per kWh';
            this.activePriceElement.innerHTML = `${formattedValue} ${type}`;
        }

        const currentDate = new Date(Date.parse(this.activeData[barIndex].date));
        const options = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        };
        const date = `op ${currentDate.toLocaleDateString('nl-NL', options)}`;
        this.activeDateElement.innerHTML = date.charAt(0)
            .toUpperCase() + date.slice(1);
        this.updateBarColor(datasetIndex, barIndex);
    }

    setInitialActiveState() {
        let currentDateLabelIndex = this.labels.findIndex((label) => label === new Date().getDate());
        if (currentDateLabelIndex === -1) {
            currentDateLabelIndex = this.labels.length - 1;
        }
        const activeDatasetIndex = this.visibleType === 'gas' ? 0 : 1;
        this.setActiveBarDataByIndex(activeDatasetIndex, currentDateLabelIndex);
    }

    initChart() {
        this.chart = new Chart(
            this.canvasElement,
            {
                type: 'bar',
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    borderColor: this.gridLineColor,
                    scales: {
                        x: {
                            grid: {
                                display: false,
                            },
                            ticks: {
                                font: {
                                    size: 14,
                                    weight: (c) => {
                                        if (c.tick.value === this.higlightIndex) return 700;
                                        return 400;
                                    },

                                },
                                color: (c) => {
                                    if (c.tick.value === this.higlightIndex) return this.activeBarColor;
                                    return '#171717';
                                },
                            },
                        },
                        y: {
                            padding: 0,
                            beginAtZero: false,
                            grace: '40%',
                            grid: {
                                color: this.gridLineColor,
                            },
                            ticks: {
                                color: '#171717',
                                font: {
                                    size: 13,
                                    weight: 400,
                                },
                                stepSize: 0.01,
                                // Include a dollar sign in the ticks
                                callback(value) {
                                    return value.toLocaleString('nl-NL', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                    });
                                },
                            },
                        },
                    },
                    onHover: (event) => {
                        this.setActiveBarData(event);
                    },
                    onResize: () => {
                        this.updateChart();
                    },
                    plugins: {
                        tooltip: {
                            enabled: false,
                        },
                        legend: {
                            display: false,
                        },
                    },
                },
                data: {
                    labels: this.labels,
                    datasets: [
                        {
                            label: 'Gasprijs per m3',
                            backgroundColor: this.defaultBarColor,
                            hoverBackgroundColor: this.activeBarColor,
                            borderRadius: {
                                topLeft: 5,
                                topRight: 5,
                                bottomLeft: 0,
                                bottomRight: 0,
                            },
                            barThickness: 16,
                            data: this.gas_prices,
                            hidden: this.visibleType === 'electricity',
                        },
                        {
                            label: 'Stroomprijs per kWh',
                            backgroundColor: this.electricityColor,
                            hoverBackgroundColor: this.activeBarColor,
                            borderRadius: {
                                topLeft: 5,
                                topRight: 5,
                                bottomLeft: 0,
                                bottomRight: 0,
                            },
                            barThickness: 16,
                            data: this.electricity_prices,
                            hidden: this.visibleType === 'gas',
                        },
                    ],
                },
            },
        );
    }

    fetchData() {
        const url = ['gas-dynamic-daily', 'electricity-dynamic-daily', 'gas-electricity-dynamic-daily'].includes(this.barType) ? 'market-price-month' : 'prices-average';
        fetch(`/ajax/canary/${url}`)
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                return Promise.reject(response);
            })
            .then((json) => {
                this.data = json;
                this.setChartData();
                return Promise.resolve(json);
            })
            .then(() => {
                this.updateChart();
                this.setInitialActiveState();
            })
            .catch(() => {
                this.error = true;
                Promise.resolve(false);
            })
            .finally(() => {
                this.loading = false;
                this.updateState();
            });
    }

    loadChart() {
        this.loading = true;
        this.error = false;
        this.updateState();
        
        if (window[this.windowKey]?.length) {
            this.data = window[this.windowKey];
            this.setChartData();
            this.updateChart();

            setTimeout(() => {
                this.setInitialActiveState();
                this.loading = false;
                this.updateState();
            }, 100);
        } else {
            this.fetchData();
        }
    }

    handleScreenWidthChange(event) {
        if (event.matches) {
            this.visibleBars = this.visibleBarsDesktop;
        } else {
            this.visibleBars = this.visibleBarsMobile;
        }
        this.updateChart();
    }

    resizeWatcher() {
        const mediaQuery = window.matchMedia('(min-width: 992px)');

        mediaQuery.addEventListener('change', () => {
            this.handleScreenWidthChange(mediaQuery);
        });
    }
}

export function barChart(chartElement, data = []) {
    Chart.register(
        BarController,
        BarElement,
        CategoryScale,
        LinearScale,
    );
    const type = chartElement.dataset.chartType;
    const chartBlockInstance = new BarChart(chartElement, data, type);
    chartBlockInstance.init();
    chartBlockInstance.resizeWatcher();
}
